

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Grid, Paper, Typography, Box, Card, CardContent, Button, alpha,
    SpeedDial, SpeedDialIcon, SpeedDialAction
} from '@mui/material';
import {
    People as PeopleIcon,
    Event as EventIcon,
    ReportProblem as GrievanceIcon,
    TrendingUp as TrendingUpIcon,
    ArrowForward as ArrowForwardIcon,
    Article as ArticleIcon,
    PhotoLibrary as PhotoLibraryIcon,
    Contacts as ContactsIcon,
    AccountBalance as ConstituencyIcon,
    Add as AddIcon,
    Settings as SettingsIcon,
    CloudUpload as CloudUploadIcon,
    Save as SaveIcon
} from '@mui/icons-material';
import TamilMonthlyCalendar from './TamilMonthlyCalendar';
import { dashboardService } from '../service/dashboardService';
import { calendarService } from '../service/api';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

const StatCard = ({ title, value, icon, color, gradient }) => (
    <Card
        sx={{
            height: '100%',
            borderRadius: 4,
            boxShadow: '0 10px 30px 0 rgba(0,0,0,0.04)',
            border: '1px solid rgba(0,0,0,0.02)',
            overflow: 'hidden',
            position: 'relative',
            background: 'white'
        }}
    >
        <CardContent sx={{ position: 'relative', zIndex: 1, p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: 3,
                        background: alpha('#E31E24', 0.08),
                        color: '#E31E24',
                        display: 'flex',
                        boxShadow: 'none',
                        border: '1px solid',
                        borderColor: alpha('#E31E24', 0.1)
                    }}
                >
                    {React.cloneElement(icon, { sx: { fontSize: '1.75rem' } })}
                </Box>
                <TrendingUpIcon sx={{ color: '#E31E24', opacity: 0.8 }} />
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary', mb: 1, fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {title}
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 900, color: '#1e293b', letterSpacing: '-1.5px' }}>
                {value}
            </Typography>
        </CardContent>
    </Card>
);

const QuickActionCard = ({ title, icon, color, gradient, onClick }) => (
    <Card
        onClick={onClick}
        sx={{
            height: '100%',
            borderRadius: 4,
            boxShadow: '0 10px 30px 0 rgba(0,0,0,0.04)',
            border: '1px solid rgba(0,0,0,0.02)',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            background: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 12px 24px rgba(0,0,0,0.08)',
                borderColor: color,
                '& .action-arrow': { transform: 'translateX(4px)', opacity: 1 }
            }
        }}
    >
        <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: 2.5,
                        background: alpha('#E31E24', 0.08),
                        color: '#E31E24',
                        display: 'flex',
                        border: '1px solid',
                        borderColor: alpha('#E31E24', 0.1)
                    }}
                >
                    {React.cloneElement(icon, { sx: { fontSize: '1.5rem' } })}
                </Box>
                <ArrowForwardIcon
                    className="action-arrow"
                    sx={{
                        color: '#E31E24',
                        opacity: 0.6,
                        fontSize: '1.4rem',
                        transition: 'all 0.3s ease'
                    }}
                />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                {title}
            </Typography>
        </CardContent>
    </Card>
);



function DashboardHome() {
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        users: 0,
        events: 0,
        grievances: 0,
        news: 0,
        calendars: 0
    });

    const quickActions = [
        { title: 'Users', icon: <PeopleIcon />, color: '#E31E24', gradient: "linear-gradient(135deg, #E31E24 0%, #a00000 100%)", path: '/users' },
        { title: 'News', icon: <ArticleIcon />, color: '#E31E24', gradient: "linear-gradient(135deg, #E31E24 0%, #a00000 100%)", path: '/news' },
        { title: 'Events', icon: <EventIcon />, color: '#E31E24', gradient: "linear-gradient(135deg, #E31E24 0%, #a00000 100%)", path: '/events' },
        { title: 'Gallery', icon: <PhotoLibraryIcon />, color: '#E31E24', gradient: "linear-gradient(135deg, #E31E24 0%, #a00000 100%)", path: '/gallery' },
        { title: 'Grievances', icon: <GrievanceIcon />, color: '#E31E24', gradient: "linear-gradient(135deg, #E31E24 0%, #a00000 100%)", path: '/grievances' },
        { title: 'Calendar', icon: <CalendarMonthIcon />, color: '#E31E24', gradient: "linear-gradient(135deg, #E31E24 0%, #a00000 100%)", path: '/calendar' },
        { title: 'Directory', icon: <ContactsIcon />, color: '#E31E24', gradient: "linear-gradient(135deg, #E31E24 0%, #a00000 100%)", path: '/directory' },
        { title: 'Constituency', icon: <ConstituencyIcon />, color: '#E31E24', gradient: "linear-gradient(135deg, #E31E24 0%, #a00000 100%)", path: '/constituency' },
    ];

    useEffect(() => {
        const fetchCounts = async () => {
            try {
                const response = await dashboardService.getCounts();
                if (response.success && response.data) {
                    setStats(prev => ({ ...prev, ...response.data }));
                }
            } catch (error) {
                console.error("Failed to fetch dashboard counts", error);
            }
        };

        const fetchCalendarCount = async () => {
            try {
                const response = await calendarService.getAll();
                if (response.success && response.data && response.data.data) {
                    setStats(prev => ({ ...prev, calendars: response.data.data.length }));
                }
            } catch (error) {
                console.error("Failed to fetch calendar counts", error);
            }
        };

        fetchCounts();
        fetchCalendarCount();
    }, []);

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    mb: 4,
                    gap: 2
                }}
            >
                <Box>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 800,
                            color: '#1e293b',
                            mb: 0.5,
                            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' }
                        }}
                    >
                        Welcome back, Admin 👋
                    </Typography>
                    {/* <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        Here's what's happening with your projects today.
                    </Typography> */}
                </Box>
                {/* <Button
                    variant="contained"
                    startIcon={<ArrowForwardIcon />}
                    sx={{
                        boxShadow: '0 10px 20px -5px rgba(79, 70, 229, 0.4)',
                        px: { xs: 2, sm: 3 },
                        py: 1,
                        width: { xs: '100%', sm: 'auto' }
                    }}
                >
                    Generate Report
                </Button> */}
            </Box>




            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "repeat(1, 1fr)",
                        sm: "repeat(2, 1fr)",
                        md: "repeat(3, 1fr)",
                    },
                    gap: { xs: 2, sm: 3 },
                    mt: 2,
                }}
            >
                {[
                    { title: "Total Users", value: stats.users || 0, icon: <PeopleIcon />, color: "#E31E24", gradient: "linear-gradient(135deg, #E31E24 0%, #a00000 100%)" },
                    { title: "Today News", value: stats.news || 0, icon: <ArticleIcon />, color: "#E31E24", gradient: "linear-gradient(135deg, #E31E24 0%, #a00000 100%)" },
                    { title: "Active Events", value: stats.events || 0, icon: <EventIcon />, color: "#E31E24", gradient: "linear-gradient(135deg, #E31E24 0%, #a00000 100%)" },
                    { title: "Grievances", value: stats.grievances || 0, icon: <GrievanceIcon />, color: "#E31E24", gradient: "linear-gradient(135deg, #E31E24 0%, #a00000 100%)" },
                    { title: "Active Calendars", value: stats.calendars || 0, icon: <CalendarMonthIcon />, color: "#E31E24", gradient: "linear-gradient(135deg, #E31E24 0%, #a00000 100%)" },
                ].map((card, index) => (
                    <StatCard
                        key={index}
                        title={card.title}
                        value={card.value}
                        icon={card.icon}
                        color={card.color}
                        gradient={card.gradient}
                    />
                ))}
            </Box>

            {/* Quick Actions Section - Desktop Only */}
            <Box sx={{
                mt: 5,
                mb: 4,
                display: { xs: 'none', md: 'block' }
            }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', mb: 3 }}>
                    Quick Actions
                </Typography>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: 3,
                    }}
                >
                    {quickActions.map((action, index) => (
                        <QuickActionCard
                            key={index}
                            title={action.title}
                            icon={action.icon}
                            color={action.color}
                            gradient={action.gradient}
                            onClick={() => navigate(action.path)}
                        />
                    ))}
                </Box>
            </Box>

            {/* Mobile/Tablet View - Quick Actions as Speed Dial (FAB) */}
            <SpeedDial
                ariaLabel="Quick Actions SpeedDial"
                sx={{
                    position: 'fixed',
                    bottom: 24,
                    right: 24,
                    display: { xs: 'flex', md: 'none' },
                    '& .MuiFab-primary': {
                        width: 56,
                        height: 56,
                        boxShadow: `0 8px 24px ${alpha('#E31E24', 0.4)}`,
                        background: 'linear-gradient(135deg, #E31E24 0%, #a00000 100%)',
                        color: 'white',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #a00000 0%, #E31E24 100%)',
                        }
                    }
                }}
                icon={<SpeedDialIcon icon={<SettingsIcon />} openIcon={<SettingsIcon />} />}
            >
                {quickActions.map((action) => (
                    <SpeedDialAction
                        key={action.title}
                        icon={action.icon}
                        tooltipTitle={action.title}
                        tooltipOpen
                        onClick={() => navigate(action.path)}
                        sx={{
                            '& .MuiSpeedDialAction-fab': {
                                bgcolor: 'white',
                                color: action.color,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                '&:hover': {
                                    bgcolor: alpha(action.color, 0.05)
                                }
                            },
                            '& .MuiSpeedDialAction-staticTooltipLabel': {
                                fontWeight: 700,
                                fontSize: '0.75rem',
                                color: '#1e293b',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                px: 1.5,
                                py: 0.5,
                                borderRadius: 2
                            }
                        }}
                    />
                ))}
            </SpeedDial>

            <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mt: { xs: 1, sm: 2 } }}>
                {/* <Grid item xs={12} md={8} lg={9}>
                    <Paper
                        sx={{
                            p: { xs: 2, sm: 3 },
                            borderRadius: 4,
                            boxShadow: '0 10px 30px 0 rgba(0,0,0,0.04)',
                            height: '100%',
                            minHeight: { xs: '300px', sm: '400px' },
                            bgcolor: 'white'
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, fontSize: { xs: '1rem', sm: '1.25rem' } }}>Overview Analysis</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '80%' }}>
                            <Box
                                sx={{
                                    width: '100%',
                                    height: { xs: '200px', sm: '300px' },
                                    bgcolor: '#f8fafc',
                                    borderRadius: 4,
                                    border: '2px dashed #e2e8f0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <Typography variant="body2" color="text.disabled">Chart Placeholder</Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid> */}
                <Grid item xs={12} md={4} lg={3}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* <TamilCalendar /> */}
                        {/* <Paper
                            sx={{
                                p: { xs: 2, sm: 3 },
                                borderRadius: 4,
                                boxShadow: '0 10px 30px 0 rgba(0,0,0,0.04)',
                                bgcolor: 'white'
                            }}
                        > */}
                        {/* <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, fontSize: { xs: '1rem', sm: '1.25rem' } }}>Recent Activity</Typography>
                            {[1, 2, 3, 4, 5].map((i) => (
                                <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', mr: 2, flexShrink: 0 }} />
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.825rem', sm: '0.875rem' } }}>New user registered</Typography>
                                        <Typography variant="caption" sx={{ color: 'text.disabled' }}>2 minutes ago</Typography>
                                    </Box>
                                </Box>
                            ))} */}
                        {/* </Paper> */}
                    </Box>
                </Grid>
            </Grid>
            {/* <Box sx={{ mt: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 3, color: '#1e3a8a', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                    Tamil Monthly Calendar View
                </Typography>
                <TamilMonthlyCalendar />
            </Box> */}
        </Box>
    );
}

export default DashboardHome;

